import { CSRFTokenField } from "@circle-react-uikit/CSRFTokenField";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import type {
  BaseSyntheticEvent,
  ComponentPropsWithoutRef,
  MutableRefObject,
  ReactNode,
} from "react";
import type {
  DeepPartial,
  DefaultValues,
  Resolver,
  SubmitHandler,
  UseFormGetValues,
  UseFormReset,
  UseFormSetError,
  UseFormSetFocus,
  UseFormSetValue,
} from "react-hook-form";
import { FormProvider, useForm, useFormState } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";

import { PreventAccidentalLeave } from "./PreventAccidentalLeave";

import "./styles.scss";
import { dirtyValues } from "./utils";

type NativeFormProps = Omit<
  ComponentPropsWithoutRef<"form">,
  "onSubmit" | "defaultValue"
>;

export type OnSubmitFunction<FormValues extends Record<string, any>> = (
  values: FormValues,
  helpers: {
    getValues: UseFormGetValues<FormValues>;
    reset: UseFormReset<FormValues>;
    setError: UseFormSetError<FormValues>;
    setFocus: UseFormSetFocus<FormValues>;
    setValue: UseFormSetValue<FormValues>;
  },
  event?: BaseSyntheticEvent,
) => Promise<void> | void;

export interface FormProps<FormValues extends Record<string, any>> extends Omit<
  NativeFormProps,
  "children"
> {
  children: ReactNode | ((props: any) => ReactNode);
  className?: string;
  debug?: boolean;
  defaultValues?: DeepPartial<FormValues>;
  formClassName?: string;
  id?: string;
  mode?: "onBlur" | "onChange" | "onSubmit" | "onTouched" | "all";
  onSubmit: OnSubmitFunction<FormValues>;
  passThroughProps?: Record<string, any>;
  preventAccidentalLeave?: boolean;
  preventAccidentalLeaveMessage?: string;
  resolver?: Resolver<FormValues>;
  shouldStopPropagation?: boolean;
  submitDirtyOnly?: boolean;
  triggerFormSubmitRef?: MutableRefObject<(() => Promise<void>) | null>;
  validationSchema?: any;
}

export const Form = <FormValues extends Record<string, any>>({
  onSubmit,
  defaultValues,
  children,
  submitDirtyOnly = false,
  preventAccidentalLeave = false,
  preventAccidentalLeaveMessage = t("prevent_accidental_leave_message"),
  validationSchema,
  resolver,
  className,
  formClassName,
  mode = "onSubmit",
  id,
  triggerFormSubmitRef,
  shouldStopPropagation = false,
  ...passThroughProps
}: FormProps<FormValues>) => {
  const methods = useForm<FormValues>({
    mode,
    reValidateMode: "onChange",
    defaultValues: defaultValues as DefaultValues<FormValues>,
    resolver:
      resolver ??
      (validationSchema ? yupResolver(validationSchema) : undefined),
  });

  const { getValues, reset, setError, setFocus, setValue } = methods;
  const { dirtyFields } = useFormState({ control: methods.control });

  const _onSubmit: SubmitHandler<any> = async (params, event) => {
    const submitData = submitDirtyOnly
      ? dirtyValues(dirtyFields, params)
      : params;

    if (isEmpty(submitData)) {
      return;
    }

    await onSubmit(
      submitData as FormValues,
      { getValues, reset, setError, setFocus, setValue },
      event,
    );
  };

  const triggerFormSubmit = methods.handleSubmit(_onSubmit);

  if (triggerFormSubmitRef) {
    triggerFormSubmitRef.current = triggerFormSubmit;
  }

  const formProviderProps = { ...methods, triggerFormSubmit };

  const renderChildren = () =>
    typeof children === "function" ? children(formProviderProps) : children;

  return (
    <div className={classNames(anchorClassNames("react-form"), className)}>
      <FormProvider {...formProviderProps}>
        {preventAccidentalLeave && (
          <PreventAccidentalLeave message={preventAccidentalLeaveMessage} />
        )}

        <form
          {...passThroughProps}
          id={id}
          className={formClassName}
          onSubmit={event => {
            if (shouldStopPropagation) {
              event.stopPropagation();
            }

            void triggerFormSubmit(event);
          }}
        >
          <CSRFTokenField />
          {renderChildren()}
        </form>
      </FormProvider>
    </div>
  );
};
